import dynamic from 'next/dynamic';
import { type FC, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import PlayButton from 'molecules/Button/PlayButton';
import Image from 'molecules/Image';
import Section from 'molecules/Section';
import { VideoConfigProps } from 'molecules/Video/config';

import { analyticsClient } from 'utils/analytics/client';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface VideoProps extends VideoConfigProps {
  className?: string;
  priority?: boolean;
  /** Render without the padded Section wrapper, for use inside a parent that already provides the container. */
  bare?: boolean;
}

const Video: FC<VideoProps> = ({ videoSrc, thumbnail, className, hasGradientTransition, darkMode, priority, bare }) => {
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!playing) {
      return;
    }

    analyticsClient.track(`Viewed Video - ${thumbnail?.alt ?? document.title}`, {});
  }, [playing, thumbnail?.alt]);

  const playAriaLabel = thumbnail?.alt ? `Play video: ${thumbnail.alt}` : 'Play video';

  const thumbWidth = thumbnail?.file?.width;
  const thumbHeight = thumbnail?.file?.height;
  const thumbnailAspect = thumbWidth && thumbHeight ? `${thumbWidth} / ${thumbHeight}` : '16 / 9';
  const aspectRatio = playing ? '16 / 9' : thumbnailAspect;

  const player = (
    <div
      className={twJoin('relative mx-auto max-w-[800px] w-full overflow-hidden rounded-lg', className)}
      style={{ aspectRatio }}
      onMouseEnter={() => setLoaded(true)}
    >
      {!playing && (
        <div className="z-10 absolute-cover">
          {thumbnail ? (
            <Image
              {...(thumbnail.file || thumbnail)}
              className="size-full"
              alt={thumbnail.alt || ''}
              priority={priority}
              objectCover
              unsetRatio
              unsetMaxW
            />
          ) : (
            <div className="size-full bg-primary-50 dark:bg-primary-700" />
          )}
          <div className="flex items-center justify-center absolute-cover">
            <PlayButton onClick={() => setPlaying(true)} ariaLabel={playAriaLabel} />
          </div>
        </div>
      )}
      {loaded && playing && (
        <ReactPlayer
          controls
          url={videoSrc}
          muted={false}
          width="100%"
          height="100%"
          playing
          className="absolute-cover"
        />
      )}
    </div>
  );

  if (bare) {
    return player;
  }

  return (
    <Section darkMode={darkMode} hasGradientTransition={hasGradientTransition}>
      {player}
    </Section>
  );
};

export default Video;
